import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, ApiError, type Tree } from "./api";

export function useTree() {
  return useQuery({ queryKey: ["tree"], queryFn: () => api.get<Tree>("/api/tree") });
}

/**
 * Executa uma alteração, guarda o erro para exibir e, no sucesso, invalida as consultas
 * afetadas. Devolve `true` se deu certo.
 */
export function useAction(invalidate: string[][] = [["tree"]]) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function run(fn: () => Promise<unknown>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await Promise.all(invalidate.map((k) => qc.invalidateQueries({ queryKey: k })));
      return true;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "não foi possível completar a operação");
      return false;
    } finally {
      setBusy(false);
    }
  }
  return { run, busy, error, clearError: () => setError(null) };
}

/** Nomes legíveis dos ids da hierarquia, a partir da árvore visível. */
export function treeIndex(tree: Tree | undefined) {
  const groups = new Map<number, string>();
  const sites = new Map<number, { name: string; group: string }>();
  const units = new Map<number, { name: string; site: string; group: string }>();
  for (const g of tree?.groups ?? []) {
    groups.set(g.id, g.name);
    for (const s of g.sites) {
      sites.set(s.id, { name: s.name, group: g.name });
      for (const u of s.units) units.set(u.id, { name: u.name, site: s.name, group: g.name });
    }
  }
  return { groups, sites, units };
}

export const TIMEZONES = [
  "America/Sao_Paulo",
  "America/Manaus",
  "America/Cuiaba",
  "America/Porto_Velho",
  "America/Rio_Branco",
  "America/Belem",
  "America/Fortaleza",
  "America/Recife",
  "America/Noronha",
  "America/Boa_Vista",
];
